import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { tailwindColors } from '../../../../../tailwind.config';

const CALCULABLE_STATUSES = new Set(['Pass', 'Alert', 'Fail']);

// Color mapping for chart data
const getChartColor = (name: string): string => {
  switch (name) {
    case 'Pass':
      return tailwindColors.pass;
    case 'Alert':
      return tailwindColors.alert;
    case 'Fail':
      return tailwindColors.fail;
    case 'N/A':
      return tailwindColors.na;
    default:
      return '#cccccc';
  }
};

type ChartDataEntry = {
  name: string;
  value: number;
};

type HalfPieChartProps = {
  data: ChartDataEntry[];
  size?: number;
};

export const HalfPieChart: React.FC<HalfPieChartProps> = ({
  data,
  size = 100,
}) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const calculableTotal = data.reduce(
    (sum, d) => (CALCULABLE_STATUSES.has(d.name) ? sum + d.value : sum),
    0,
  );

  // Updated passValue to sum all 'Pass' and 'Alert' values
  const passValue = data.reduce((sum, d) => {
    if (d.name === 'Pass' || d.name === 'Alert') {
      return sum + d.value;
    }
    return sum;
  }, 0);

  const isEmpty = total === 0;
  const isPassRateNA = !isEmpty && calculableTotal === 0;
  const passPct =
    calculableTotal > 0 ? (passValue / calculableTotal) * 100 : 0;
  const pieData = isEmpty ? [{ name: 'empty', value: 1 }] : data;

  return (
    <div
      className="relative flex cursor-pointer flex-col items-center justify-center"
      style={{ width: size }}
    >
      {/* Gauge */}
      <ResponsiveContainer
        width="100%"
        height={size / 2}
        className="cursor-pointer"
      >
        <PieChart className="cursor-pointer">
          <Pie
            data={pieData.map((entry) => ({
              ...entry,
              fullData: data,
            }))}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="100%"
            startAngle={180}
            endAngle={0}
            outerRadius={size / 2.5}
            innerRadius={size / 4}
            cornerRadius={4}
            paddingAngle={isEmpty ? 0 : 1}
            cursor={'pointer'}
          >
            {pieData.map((entry) => (
              <Cell
                key={entry.name}
                fill={isEmpty ? '#e5e7eb' : getChartColor(entry.name)}
                cursor={'pointer'}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      <div className="-mt-2 cursor-pointer text-center">
        <p
          className={`cursor-pointer text-xs font-bold leading-tight ${isEmpty || isPassRateNA ? 'text-gray-500' : 'text-gray-800'}`}
        >
          {isEmpty || isPassRateNA ? 'N/A' : `${Math.round(passPct)}%`}
        </p>
      </div>
    </div>
  );
};
